import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart, type BarDatum } from '../../components/charts/BarChart';
import { DonutChart } from '../../components/charts/DonutChart';
import { GaugeBar } from '../../components/charts/GaugeBar';
import { Sparkline } from '../../components/charts/Sparkline';
import { TrendChart } from '../../components/charts/TrendChart';
import { chartColour, topN } from '../../components/charts/palette';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonBlock, SkeletonText } from '../../components/Skeleton';
import { formatDate } from '../../i18n';
import { UpcomingAppointmentsCard } from '../appointments/UpcomingAppointmentsCard';
import { RecentActivityCard } from '../audit/RecentActivityCard';
import { useSession } from '../auth/queries';
import { ApprovalInbox } from '../tickets/approvals/ApprovalInbox';
import { toneForStateCategory } from '../tickets/presentation';
import type { StateCategory } from '../tickets/api';
import { MyWorkCard } from './MyWorkCard';
import { StatTile } from './StatTile';
import { TILE_KEYS } from './tiles';
import { daysAgo, fillDays, formatReportValue, isoDate } from './presentation';
import {
  useCreateTile,
  useDashboard,
  useDashboardTiles,
  useDeleteTile,
  useReport,
} from './queries';

/**
 * Die Startseite -- F5-05, K-121.
 *
 * **Vier Zahlen und vier Bilder.** Die Zahlen beantworten "wie viel jetzt":
 * Wie viel liegt an, wie viel davon ist ueberfaellig, wie viel kam gestern
 * herein, wie viel Zeit ist diese Woche erfasst. Die Bilder beantworten
 * "wohin geht es": der Verlauf des Aufkommens, die Verteilung der offenen
 * Vorgaenge, die Einhaltung der Zusagen je Kunde, die Auslastung je
 * Bearbeiter.
 *
 * Die Trennung ist keine Gestaltungsfrage. Wer nur Zahlen hat, merkt einen
 * Anstieg an dem Tag, an dem er ihn nicht mehr abarbeiten kann; wer nur
 * Bilder hat, weiss nie, ob "viel" dreissig oder dreihundert bedeutet.
 *
 * **Die ueberfaelligen Vorgaenge sind die einzige Zahl mit Farbe.** Eine
 * Startseite, auf der alles bunt ist, hat keine Aussage mehr. In den
 * Diagrammen traegt Farbe ohnehin nie allein die Aussage -- jeder Balken,
 * jedes Segment hat seine Beschriftung daneben.
 *
 * **Welche Kacheln erscheinen, entscheidet der Nutzer** (F5-05). Wer noch
 * nichts eingestellt hat, sieht alle -- eine leere Startseite waere fuer
 * einen neuen Kollegen das Gegenteil einer Hilfe. Beim ersten Abwaehlen
 * werden deshalb die uebrigen ausdruecklich festgehalten: Sonst hiesse
 * "keine Zeile in der Tabelle" abwechselnd "alles" und "nichts".
 *
 * Die drei Diagramme, die einen eigenen Bericht brauchen, fragen ihn nur ab,
 * wenn ihre Kachel gewaehlt ist. Drei Auswertungen, die niemand ansieht, sind
 * drei Abfragen, die trotzdem jemand bezahlt.
 */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const dashboard = useDashboard();
  const tiles = useDashboardTiles();
  const createTile = useCreateTile();
  const deleteTile = useDeleteTile();

  const configured = tiles.data ?? [];
  // Ohne Einstellung alle: Eine leere Startseite waere fuer einen neuen
  // Kollegen das Gegenteil einer Hilfe.
  const shown = (key: string) =>
    configured.length === 0 || configured.some((tile) => tile.tile_key === key);

  // Dreissig Tage: kurz genug, dass die Linie noch Tage zeigt, und lang
  // genug, dass ein Muster ueber die Woche hinaus sichtbar wird.
  const since = daysAgo(29);
  const until = daysAgo(0);
  const period = { since: isoDate(since), until: isoDate(until) };

  // **Erst wenn die Kachelliste da ist.** Solange sie unterwegs ist, gilt
  // "keine Zeile" als "alle" -- und drei Berichte liefen los, die der Nutzer
  // vielleicht laengst abgewaehlt hat. Eine Abfrage zurueckzunehmen geht
  // nicht; sie nicht zu stellen schon.
  const decided = !tiles.isPending;

  const volume = useReport('ticket_volume', 'day', period, {
    enabled: decided && (shown('volume_trend') || shown('created_yesterday')),
  });
  const compliance = useReport('sla_compliance', 'customer', period, {
    enabled: decided && shown('sla_compliance'),
  });
  const workload = useReport('workload', 'owner', period, {
    enabled: decided && shown('workload'),
  });

  const toggle = (key: string) => {
    const existing = configured.find((tile) => tile.tile_key === key);
    if (existing) {
      deleteTile.mutate(existing.id);
      return;
    }
    if (configured.length === 0) {
      // Erstes Abwaehlen: Die uebrigen ausdruecklich festhalten, sonst hiesse
      // "keine Zeile" gleich darauf wieder "alle".
      for (const other of TILE_KEYS) {
        if (other !== key) createTile.mutate({ tile_key: other });
      }
      return;
    }
    createTile.mutate({ tile_key: key });
  };

  if (dashboard.isPending) return <DashboardSkeleton />;
  if (dashboard.isError) return <ErrorNotice error={dashboard.error} />;

  const data = dashboard.data;
  const days = fillDays(volume.data?.rows ?? [], since, until);
  const weekOverWeek = compareLastWeeks(days);

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        action={
          <Link to="/app/reports" className="nav__item">
            {t('reports.title')}
          </Link>
        }
      />

      {/* Ganz oben und nicht als Kachel: Wer blockiert, soll es zuerst
          sehen -- und es nicht abschalten koennen. */}
      <ApprovalInbox />

      <div className="kpi-grid">
        {shown('awaiting_reply') ? (
          // F1-50. Sie fuehrt in genau die Liste, die sie zaehlt -- offen
          // und wartend, so wie der Zaehler selbst rechnet.
          <StatTile
            label={t('dashboard.awaitingReply')}
            value={String(data.awaiting_reply)}
            hint={t('tickets.awaitingReplyHint')}
            tone={data.awaiting_reply > 0 ? 'danger' : undefined}
            to="/app/tickets"
            search={{ unresolved: true, awaiting: true }}
          />
        ) : null}
        {shown('open_tickets') ? (
          // Die Kachel fuehrt in genau die Liste, die sie zaehlt. Ohne diesen
          // Weg ist eine Kennzahl eine Zahl zum Ansehen: Man weiss, dass
          // zwoelf offen sind, und sucht sie danach von Hand.
          <StatTile
            label={t('dashboard.openTickets')}
            value={String(data.open_tickets)}
            hint={t('dashboard.openTicketsHint')}
            to="/app/tickets"
            // ``unresolved`` und nicht ``state=open``: Die Kachel zaehlt
            // alles ausser Gelöst und Geschlossen, die Zustandsgruppe
            // ``open`` heisst dagegen "In Bearbeitung". Mit ihr zeigte der
            // Verweis weniger Zeilen, als die Zahl darueber versprach.
            search={{ unresolved: true }}
          />
        ) : null}
        {shown('breached_tickets') ? (
          <StatTile
            label={t('dashboard.breachedTickets')}
            value={String(data.breached_tickets)}
            tone={data.breached_tickets > 0 ? 'danger' : undefined}
            hint={t('dashboard.breachedHint')}
          />
        ) : null}
        {shown('created_yesterday') ? (
          <StatTile
            label={t('dashboard.createdYesterday')}
            value={String(data.created_yesterday)}
            hint={t('dashboard.recentTrend')}
            // Der Vergleich steht nur da, wenn er sich aus den Daten
            // ergibt: sieben Tage gegen die sieben davor. Ist der
            // Bericht noch unterwegs oder liegen weniger als vierzehn
            // Tage vor, erscheint keine Richtung -- und keine Null, die
            // aussaehe wie "unveraendert".
            comparison={
              weekOverWeek === null
                ? undefined
                : {
                    percent: weekOverWeek,
                    label: t('dashboard.deltaWeek'),
                    moreIsBetter: false,
                  }
            }
          >
            {/* Der Strich zeigt die Richtung, die Zahl darueber den Stand.
                Vierzehn Tage, weil dreissig auf drei Zentimetern Breite zu
                einem Zittern werden. */}
            <span className="kpi__spark">
              <Sparkline values={days.slice(-14).map((day) => day.value)} />
            </span>
          </StatTile>
        ) : null}
        {shown('minutes_this_week') ? (
          <StatTile
            label={t('dashboard.minutesThisWeek')}
            value={formatReportValue(data.minutes_this_week, 'minutes')}
            hint={t('time.hours')}
            to="/app/time-report"
          />
        ) : null}
      </div>

      {/* Was auf dem eigenen Namen liegt -- vor den Diagrammen. Die
          Auswertungen sagen, wie es dem Betrieb geht; diese Liste sagt, was
          der Betrachter selbst zu tun hat. Die zweite Frage ist morgens um
          acht die dringendere. */}
      <MyWorkCard />

      {/* Heute und morgen -- F1-53. Neben "Meine Arbeit" die zweite Frage
          des Morgens: nicht was liegt an, sondern wo muss ich hin. */}
      <UpcomingAppointmentsCard />

      <div className="chart-grid">
        {shown('volume_trend') ? (
          <ChartCard title={t('dashboard.trend')} hint={t('dashboard.trendHint')}>
            {volume.isError ? <ErrorNotice error={volume.error} /> : null}
            {days.length > 0 ? (
              <TrendChart
                data={days.map((day) => ({
                  key: day.key,
                  label: formatDate(day.key, i18n.language),
                  value: day.value,
                }))}
                caption={t('dashboard.trend')}
                maxLabel={`${t('reportUnit.count')}: ${String(
                  Math.max(...days.map((day) => day.value), 0),
                )}`}
              />
            ) : null}
          </ChartCard>
        ) : null}

        {shown('by_state_category') ? (
          <ChartCard title={t('dashboard.byState')} hint={t('dashboard.byStateHint')}>
            {data.by_state_category.length === 0 ? (
              <EmptyState title={t('dashboard.noChartData')} hint={t('dashboard.noChartHint')} />
            ) : (
              <DonutChart
                caption={t('dashboard.byState')}
                total={String(data.open_tickets)}
                totalLabel={t('charts.total')}
                data={data.by_state_category.map((entry) => ({
                  key: entry.key,
                  label: t(`stateCategory.${entry.key}`),
                  value: entry.count,
                  colour: stateColour(entry.key as StateCategory),
                }))}
              />
            )}
          </ChartCard>
        ) : null}
      </div>

      <div className="chart-grid">
        {shown('sla_compliance') ? (
          <ChartCard title={t('dashboard.slaCompliance')} hint={t('dashboard.slaHint')}>
            {compliance.isError ? <ErrorNotice error={compliance.error} /> : null}
            {compliance.data && compliance.data.rows.length > 0 ? (
              <div className="stack">
                {compliance.data.rows.slice(0, 6).map((row) => (
                  <GaugeBar
                    key={row.key}
                    label={row.label}
                    percent={row.value}
                    display={formatReportValue(row.value, 'percent')}
                    tone={row.value >= 95 ? 'success' : row.value >= 80 ? 'warning' : 'danger'}
                  />
                ))}
              </div>
            ) : (
              <EmptyState title={t('dashboard.noChartData')} hint={t('dashboard.noChartHint')} />
            )}
          </ChartCard>
        ) : null}

        {shown('workload') ? (
          <ChartCard title={t('dashboard.workload')} hint={t('dashboard.workloadHint')}>
            {workload.isError ? <ErrorNotice error={workload.error} /> : null}
            {workload.data && workload.data.rows.length > 0 ? (
              <BarChart
                caption={t('dashboard.workload')}
                data={toBars(workload.data.rows, 'minutes', (count) =>
                  t('charts.other', { count }),
                )}
              />
            ) : (
              <EmptyState title={t('dashboard.noChartData')} hint={t('dashboard.noChartHint')} />
            )}
          </ChartCard>
        ) : null}
      </div>

      {/* Letzte Aktivitaeten -- F5-02. Unten und nicht oben: Sie beantwortet
          "was ist passiert" und nicht "was ist zu tun"; die zweite Frage ist
          morgens um acht die dringendere. Nur fuer die, die das Protokoll
          sehen duerfen -- die Kachel haengt an ``privacy.manage`` wie die
          Sektion selbst. */}
      {shown('recent_activity') && (session.data?.permissions ?? []).includes('privacy.manage') ? (
        <RecentActivityCard />
      ) : null}

      {/* Ohne eigenen Zustand: ``<details>`` klappt von selbst auf und zu.
          Ein gesteuertes ``open`` neben dem eingebauten Verhalten schaltete
          zweimal je Klick -- der Kasten blieb geschlossen, und die Auswahl
          darin war im Browser nicht erreichbar. */}
      <details>
        <summary className="table__subject">{t('dashboard.choose')}</summary>
        <section className="card">
          <p className="table__muted">{t('dashboard.chooseHint')}</p>
          {TILE_KEYS.map((key) => (
            <label className="field__checkbox" key={key}>
              <input
                type="checkbox"
                checked={shown(key)}
                onChange={() => {
                  toggle(key);
                }}
              />
              {t(`dashboardTile.${key}`)}
            </label>
          ))}
        </section>
      </details>
    </>
  );
}

/**
 * Die Farbe eines Zustands im Ring.
 *
 * Sie folgt dem Ton, den derselbe Zustand als Kennzeichen traegt: Wer
 * "Gelöst" im Ticket gruen gesehen hat, sucht es im Ring nicht in Blau. Fuer
 * die Ringfarbe braucht es einen Farbwert und keine Tonbezeichnung -- deshalb
 * diese Zuordnung und nicht ``chartColour``.
 */
function stateColour(category: StateCategory): string {
  switch (toneForStateCategory(category)) {
    case 'success':
      return 'var(--color-success)';
    case 'warning':
      return 'var(--color-warning)';
    case 'danger':
      return 'var(--color-danger)';
    case 'info':
      return 'var(--chart-1)';
    default:
      return 'var(--chart-8)';
  }
}

/** Berichtszeilen als Balken -- die groessten acht, der Rest zusammengefasst. */
function toBars(
  rows: readonly { key: string; label: string; value: number }[],
  unit: string,
  restLabel: (count: number) => string,
): BarDatum[] {
  const trimmed = topN(
    rows.map((row) => ({ key: row.key, label: row.label, value: row.value })),
    8,
    (value, count) => ({ key: '__rest__', label: restLabel(count), value }),
  );

  return trimmed.map((row, index) => ({
    key: row.key,
    label: row.label,
    value: row.value,
    display: formatReportValue(row.value, unit),
    colour: row.key === '__rest__' ? 'var(--chart-8)' : chartColour(index),
  }));
}

function ChartCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section className="card">
      <div className="chart-card__head">
        <h2>{title}</h2>
        <p className="chart-card__hint">{hint}</p>
      </div>
      {children}
    </section>
  );
}

/**
 * Der Unterschied der letzten sieben Tage zu den sieben davor, in Prozent.
 *
 * ``null``, wenn er sich nicht berechnen laesst: Liegen weniger als vierzehn
 * Tage vor oder war die Vorwoche leer, gibt es keinen Vergleich -- und keine
 * erfundene Richtung. Eine Steigerung von null auf drei ist nicht
 * "unendlich Prozent mehr", sondern eine Aussage, die die Daten nicht
 * hergeben.
 */
function compareLastWeeks(days: readonly { value: number }[]): number | null {
  if (days.length < 14) return null;

  const recent = days.slice(-7).reduce((sum, day) => sum + day.value, 0);
  const before = days.slice(-14, -7).reduce((sum, day) => sum + day.value, 0);

  if (before === 0) return null;
  return ((recent - before) / before) * 100;
}

/**
 * Die Startseite, solange die Zahlen unterwegs sind.
 *
 * In der Form, die gleich kommt: vier Kacheln, zwei Diagrammflaechen. So
 * springt beim Eintreffen der Daten nichts, und wer die Seite kennt, sieht
 * schon, wo gleich was steht.
 */
function DashboardSkeleton() {
  return (
    <div aria-busy="true">
      <div className="kpi-grid">
        {[0, 1, 2, 3].map((index) => (
          <section className="card kpi" key={index}>
            <SkeletonText lines={2} />
          </section>
        ))}
      </div>
      <div className="chart-grid" style={{ marginTop: 'var(--space-4)' }}>
        {[0, 1].map((index) => (
          <section className="card" key={index}>
            <SkeletonBlock height="12rem" />
          </section>
        ))}
      </div>
    </div>
  );
}
